import React, { useMemo, useRef, useState } from "react";
import type { MenuProps } from "antd";
import {
  Button,
  Card,
  Dropdown,
  Empty,
  Row,
  Col,
  Space,
  Typography,
  message,
  Table,
  Tag,
  Input,
  DatePicker,
  Select,
  Pagination,
  Form,
} from "antd";
import {
  DownOutlined,
  MailOutlined,
  MessageOutlined,
  WechatOutlined,
  PlusOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { ChannelEnum } from "../constants/enums";
import { ChannelKeyMap, ChannelMap } from "../constants/labels";
import { CHANNEL_OPTIONS } from "../utils/options";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type TemplateKind = "sms" | "whatsapp" | "email";

type TemplateRow = {
  key: string;
  name: string;
  channel: ChannelEnum;
  createdAt: string; // ISO
  updatedAt: string; // ISO
  status: "Active" | "Inactive";
};

const channelIcon = (c: ChannelEnum) => {
  switch (c) {
    case ChannelEnum.Email:
      return <i className="icon-mail" />;
    case ChannelEnum.Sms:
      return <i className="icon-sms" />;
    case ChannelEnum.WhatsAppStandard:
      return <i className="icon-whatsapp" />;
    default:
      return <i className="icon-whatsapp" />;
  }
};

const DUMMY_DATA: TemplateRow[] = Array.from({ length: 23 }).map((_, i) => {
  const channel = [
    ChannelEnum.Sms,
    ChannelEnum.Email,
    ChannelEnum.WhatsAppStandard,
  ][i % 3];
  const status: "Active" | "Inactive" = i % 4 === 0 ? "Inactive" : "Active";
  const base = dayjs("2025-06-12 09:00").add(i % 5, "day");
  return {
    key: String(i + 1),
    name: `Template ${i + 1}`,
    channel,
    status,
    createdAt: base.toISOString(),
    updatedAt: base.add(i % 3, "day").toISOString(),
  };
});

const MessageTemplates: React.FC = () => {
  // ===== Actions =====
  const handleCreate = (channel: ChannelEnum) => {};

  const createMenuItems: MenuProps["items"] = [
    {
      key: "sms",
      label: (
        <Space>
          <i className="icon-sms" />
          <span>SMS</span>
        </Space>
      ),
      onClick: () => handleCreate(ChannelEnum.Sms),
    },
    {
      key: "whatsapp",
      label: (
        <Space>
          <i className="icon-whatsapp" />
          <span>WhatsApp</span>
        </Space>
      ),
      onClick: () => handleCreate(ChannelEnum.WhatsAppStandard),
    },
    {
      key: "email",
      label: (
        <Space>
          <i className="icon-mail" />
          <span>Email</span>
        </Space>
      ),
      onClick: () => handleCreate(ChannelEnum.Email),
    },
  ];

  // ===== Filters & Table state =====
  const [query, setQuery] = useState("");

  const [channel, setChannel] = useState<ChannelEnum[]>([]);
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [start, setStart] = useState<Dayjs | null>(null);
  const [end, setEnd] = useState<Dayjs | null>(null);
  const endRef = useRef<any>(null);

  const filtered = useMemo(() => {
    return DUMMY_DATA.filter((r) => {
      const nameOk = r.name.toLowerCase().includes(query.toLowerCase());
      const channelOk = channel.length === 0 || channel.includes(r.channel);
      const created = dayjs(r.createdAt);
      const dateOk =
        start && end
          ? created.isAfter(start.startOf("day")) &&
            created.isBefore(end.endOf("day"))
          : true;
      return nameOk && channelOk && dateOk;
    });
  }, [query, channel, start, end]);

  const total = filtered.length;
  const paged = useMemo(() => {
    const from = (page - 1) * pageSize;
    return filtered.slice(from, from + pageSize);
  }, [filtered, page, pageSize]);

  React.useEffect(() => setPage(1), [query, channel, range, pageSize]);

  const columns = [
    {
      title: "Template Name",
      dataIndex: "name",
      key: "name",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Channel",
      dataIndex: "channel",
      key: "channel",
      render: (ch: ChannelEnum) => (
        <Space>
          {channelIcon(ch)}
          {ChannelKeyMap[ch]}
        </Space>
      ),
      filters: [
        { text: ChannelKeyMap[ChannelEnum.Sms], value: ChannelEnum.Sms },
        {
          text: ChannelKeyMap[ChannelEnum.WhatsAppStandard],
          value: ChannelEnum.WhatsAppStandard,
        },
        { text: ChannelKeyMap[ChannelEnum.Email], value: ChannelEnum.Email },
      ],
      onFilter: (value: string | number | boolean, record: TemplateRow) =>
        record.channel === value,
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v: string) => dayjs(v).format("DD MMM YYYY"),
      sorter: (a: TemplateRow, b: TemplateRow) =>
        dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: TemplateRow["status"]) => (
        <Tag color={s === "Active" ? "green" : "red"}>{s}</Tag>
      ),
      filters: [
        { text: "Active", value: "Active" },
        { text: "Inactive", value: "Inactive" },
      ],
      onFilter: (value: string | number | boolean, record: TemplateRow) =>
        record.status === value,
    },
    {
      title: "Action",
      key: "action",
      align: "right" as const,
      render: (_: unknown, record: TemplateRow) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "view",
                label: "View",
                icon: <i className="icon-view-black" />,
                onClick: () => message.info(`View ${record.name}`),
              },
              {
                key: "edit",
                label: "Edit",
                icon: <i className="icon-edit-black" />,

                onClick: () => message.info(`Edit ${record.name}`),
              },
              {
                key: "delete",
                label: "Delete",
                icon: <i className="icon-delete-black" />,

                onClick: () => message.warning(`Deleted ${record.name}`),
              },
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const disabledStartDate = (current: Dayjs) => {
    if (!end) return false;
    return current.isAfter(end, "day");
  };
  const disabledEndDate = (current: Dayjs) => {
    if (!start) return false;
    return current.isBefore(start, "day");
  };
  const toolbar = (
    <Form layout="vertical">
      <Row gutter={[16, 16]} wrap align="middle">
        <Col xs={24} md={12} xl={8}>
          <Form.Item label="Search" className="label-filter">
            <Input.Search
              allowClear
              placeholder="Search template name"
              onSearch={setQuery}
              onChange={(e) => setQuery(e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} xl={8}>
          <Form.Item label="Channel" className="label-filter">
            <Select<ChannelEnum[]>
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              style={{ width: "100%" }}
              value={channel}
              onChange={(v) => setChannel(v)}
              placeholder="All Channel"
              options={CHANNEL_OPTIONS}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} lg={4}>
          <Form.Item label="From Date" className="label-filter">
            <DatePicker
              style={{ width: "100%", height: "unset !important" }}
              value={start}
              format="DD MMM YYYY"
              placeholder="MM - DD - YYYY"
              disabledDate={disabledStartDate}
              onChange={(val) => {
                setStart(val);
                setTimeout(() => endRef.current?.focus(), 0);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} lg={4}>
          <Form.Item label="To Date" className="label-filter">
            <DatePicker
              style={{ width: "100%", height: "unset !important" }}
              value={end}
              format="DD MMM YYYY"
              placeholder="MM - DD - YYYY"
              disabledDate={disabledEndDate}
              onChange={(val) => setEnd(val)}
              ref={endRef}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );

  const paginationControls = (
    <Row
      justify="space-between"
      align="middle"
      style={{ marginTop: 12 }}
      gutter={[12, 12]}
    >
      <Col>
        <Space>
          <Text type="secondary">Results per Page</Text>
          <Select
            value={pageSize}
            onChange={(v) => setPageSize(v)}
            options={[
              { value: 10, label: "10" },
              { value: 20, label: "20" },
              { value: 50, label: "50" },
            ]}
            style={{ width: 80 }}
          />
          <Text type="secondary">of {total} items</Text>
        </Space>
      </Col>
      <Col>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          onChange={(p) => setPage(p)}
          showSizeChanger={false}
        />
      </Col>
    </Row>
  );

  const hasData = DUMMY_DATA.length > 0;

  return (
    <div>
      <Card
        title={
          <Row justify="space-between" align="middle">
            <Col xs={24} md={12} xl={12}>
              <span style={{ display: "flex", alignItems: "center" }}>
                <span style={{ fontSize: 20 }}>Message Templates</span>
              </span>
            </Col>
            <Col xs={24} md={12} xl={12} style={{ textAlign: "right" }}>
              <Dropdown
                menu={{ items: createMenuItems }}
                placement="bottomRight"
                trigger={["click"]}
              >
                <Button type="primary" icon={<PlusOutlined />}>
                  Create Templates <DownOutlined />
                </Button>
              </Dropdown>
            </Col>
          </Row>
        }
        style={{ marginTop: 16 }}
      >
        {hasData ? (
          <>
            <div style={{ marginBottom: 12 }}>{toolbar}</div>
            <Table
              rowKey="key"
              dataSource={paged}
              columns={columns as any}
              pagination={false}
              scroll={{ x: "max-content" }}
            />
            {paginationControls}
          </>
        ) : (
          <div
            style={{
              minHeight: 360,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Space
              direction="vertical"
              align="center"
              size="large"
              style={{ textAlign: "center" }}
            >
              <Empty description={false} />
              <div>
                <Text type="secondary">
                  There are no templates yet. Create and save templates to reuse
                  for your SMS, Email, or WhatsApp campaigns.
                </Text>
              </div>
              <Dropdown
                menu={{ items: createMenuItems }}
                placement="bottom"
                trigger={["click"]}
              >
                <Button type="primary">Create Templates</Button>
              </Dropdown>
            </Space>
          </div>
        )}
      </Card>
    </div>
  );
};

export default MessageTemplates;
